import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui";
import "./SearchSelectModal.css";

const SearchSelectModal = ({
  show,
  onHide,
  title,
  placeholder,
  query,
  onQueryChange,
  loading = false,
  options = [],
  optionKey = (opt) => opt.id,
  optionLabel = (opt) => opt.name,
  optionSubLabel = (opt) => opt.composition,
  emptyMessage,
  loadingMessage,
  hint,
  onSelect,
  minQueryLength = 0,
  headerContent = null,
}) => {
  const { t } = useTranslation("layout");
  const resolvedTitle = title ?? t("searchSelectModal.title");
  const resolvedPlaceholder = placeholder ?? t("searchSelectModal.placeholder");
  const resolvedEmpty = emptyMessage ?? t("searchSelectModal.empty");
  const resolvedLoading = loadingMessage ?? t("searchSelectModal.loading");
  // Подсказка о минимальной длине запроса формируется здесь из minQueryLength,
  // поэтому внешний hint нужен только для своих текстов (например, ошибки
  // поиска) — иначе одно и то же сообщение показывалось дважды.
  const minQueryText =
    minQueryLength > 0
      ? t("searchSelectModal.minQuery", { count: minQueryLength })
      : "";
  const [activeIndex, setActiveIndex] = useState(0);

  const isQueryEnough = (query || "").trim().length >= minQueryLength;

  const visibleOptions = useMemo(() => {
    if (!isQueryEnough) return [];
    return options || [];
  }, [options, isQueryEnough]);

  useEffect(() => {
    setActiveIndex(0);
  }, [show, query, visibleOptions.length]);

  const handleKeyDown = (e) => {
    if (!show) return;
    if (!isQueryEnough) return;
    if (!visibleOptions.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, visibleOptions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = visibleOptions[activeIndex];
      if (opt) onSelect(opt);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="search-select-modal">
      <Modal.Header closeButton>
        <Modal.Title>{resolvedTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body onKeyDown={handleKeyDown}>
        {headerContent ? (
          <div className="mb-3" onKeyDown={(e) => e.stopPropagation()}>
            {headerContent}
          </div>
        ) : null}
        <Form.Control
          autoFocus
          type="text"
          value={query}
          placeholder={resolvedPlaceholder}
          onChange={(e) => onQueryChange(e.target.value)}
        />

        {hint ? (
          <div
            className={`pick-modal__hint${
              isQueryEnough ? " is-error" : ""
            }`}>
            {hint}
          </div>
        ) : null}

        {loading ? (
          <div className="search-select-modal__state d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span>{resolvedLoading}</span>
          </div>
        ) : !isQueryEnough ? (
          minQueryText ? (
            <div className="search-select-modal__state">{minQueryText}</div>
          ) : null
        ) : visibleOptions.length > 0 ? (
          <ul className="search-select-modal__list">
            {visibleOptions.map((opt, idx) => (
              <li key={optionKey(opt)}>
                <button
                  type="button"
                  className={`search-select-modal__item${
                    idx === activeIndex ? " is-active" : ""
                  }`}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => onSelect(opt)}>
                  <div className="search-select-modal__item-title">
                    {optionLabel(opt)}
                  </div>
                  {optionSubLabel(opt) ? (
                    <div className="search-select-modal__item-sub">
                      {optionSubLabel(opt)}
                    </div>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="search-select-modal__state">{resolvedEmpty}</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t("searchSelectModal.close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SearchSelectModal;

